import { FC, useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { CurrencyInput } from 'components/CurrencyInput';
import { TIP_SELECTOR_TEST_ID } from 'routes/Checkout/ReviewAndPayment/TipSelector/const';
import { TipSuggestion } from 'olo-service';

import styles from './TipSelector.module.scss';
import cx from 'clsx';
import { TipOption } from './TipOption';
import { Button, ButtonVariant } from '@clover/web-lib-consumer-ui';

export type TipSelectorProps = {
  className?: string;
  onTipSelect: (tipAmount: number) => void;
  tipSuggestions: TipSuggestion[];
  defaultTipSuggestion?: TipSuggestion;
  totalAmount: number;
  showNoneTip?: boolean;
  locale?: string;
};

const CUSTOM = 'custom';
const NONE = 'none';
const MAX_TIP_VALUE = 999999.99;

// extracted out for testing
export const calculateTipAmount = (totalAmount: number, tipPercentage: number) => Number((totalAmount * tipPercentage).toFixed(2));

export const TipSelector: FC<TipSelectorProps> = props => {
  const { totalAmount, tipSuggestions, defaultTipSuggestion, onTipSelect, showNoneTip = true, locale = 'en-US' } = props;
  const { t } = useTranslation();

  const sortedTipSuggestions: TipSuggestion[] = useMemo(() => tipSuggestions
    .map(tip => ({ ...tip, percentage: tip.percentage / 100 }))
    .sort((a, b) => a.percentage - b.percentage), [tipSuggestions]);

  const [currentSelectedButton, setCurrentSelectedButton] = useState<Uuid[]>([]);
  const [customTipAmount, setCustomTipAmount] = useState<number>(0);
  const [tipPercentage, setTipPercentage] = useState<number>(0);

  // set default tip
  useEffect(() => {
    if (defaultTipSuggestion != null && sortedTipSuggestions.find(tip => tip.id === defaultTipSuggestion.id)) {
      setCurrentSelectedButton([defaultTipSuggestion.id]);
      setTipPercentage(defaultTipSuggestion.percentage / 100);
    } else if (sortedTipSuggestions.length > 0){
      setCurrentSelectedButton([sortedTipSuggestions[0].id]);
      setTipPercentage(sortedTipSuggestions[0].percentage);
    } else {
      setCurrentSelectedButton([CUSTOM]);
    }
  }, [sortedTipSuggestions, defaultTipSuggestion]);

  useEffect(() => {
    if (currentSelectedButton.includes(CUSTOM)) {
      onTipSelect(customTipAmount);
    } else if (currentSelectedButton.includes(NONE)) {
      onTipSelect(0);
    } else {
      onTipSelect(calculateTipAmount(totalAmount, tipPercentage));
    }
  }, [currentSelectedButton, customTipAmount, onTipSelect, tipPercentage, totalAmount]);

  const handleTipSelect = useCallback((id: Uuid, percentage: number) => {
    setCustomTipAmount(0);
    if (currentSelectedButton.includes(id)) {
      setCurrentSelectedButton([]);
      setTipPercentage(0);
    } else {
      setCurrentSelectedButton([id]);
      setTipPercentage(percentage);
    }
  }, [currentSelectedButton]);

  const resetToZeroTipState = useCallback((currentButton) => {
    if (currentSelectedButton.includes(currentButton)) {
      setTipPercentage(0);
      onTipSelect(0);
      setCustomTipAmount(0);
      setCurrentSelectedButton([]);

      return;
    }
    onTipSelect(0);
    setCurrentSelectedButton([currentButton]);
  }, [currentSelectedButton, onTipSelect]);

  const isCustomButtonActive = currentSelectedButton.includes(CUSTOM);
  const isNoneButtonActive = currentSelectedButton.includes(NONE);

  return (
    <>
      <div className={styles.tipSelectorWrapper} role="radiogroup">
        {!!sortedTipSuggestions.length && showNoneTip &&
          <TipOption
            data-testid={TIP_SELECTOR_TEST_ID.noneOption}
            onClick={() => resetToZeroTipState(NONE)}
            isActive={isNoneButtonActive}
          >
            <span className={styles.percentage}>{t('none')}</span>
          </TipOption>
        }

        {sortedTipSuggestions.map(({ id, percentage, name }) => {
          const isButtonActive = currentSelectedButton.includes(id);

          return (
            <TipOption
              key={id}
              data-testid={name}
              onClick={() => handleTipSelect(id, percentage)}
              isActive={isButtonActive}
            >
              <span className={styles.percentage}>{t('percentage', { percentage })}</span>
              <span className={styles.money}>{t('money', { amount: calculateTipAmount(totalAmount, percentage) })}</span>
            </TipOption>
          );
        })}
      </div>
      {!isCustomButtonActive && !!sortedTipSuggestions.length &&
        <Button
          variant={ButtonVariant.TEXT}
          className={cx(styles.customButton)}
          onClick={() => resetToZeroTipState(CUSTOM)}
          data-testid={TIP_SELECTOR_TEST_ID.customOption}
          role="radio"
          type="button"
          aria-checked="true"
        >{t('custom_tip')} +</Button>
      }
      {(isCustomButtonActive || !sortedTipSuggestions.length) &&
        <CurrencyInput
          locale={locale}
          className={styles.tipInputWithButtons}
          data-testid={TIP_SELECTOR_TEST_ID.customTipInput}
          max={MAX_TIP_VALUE}
          onValueChange={() => null}
          onBlurOut={setCustomTipAmount}
          value={customTipAmount}
        />
      }
    </>
  );
};
